import { Audio } from '@rhg/components/media/audio-player/audio';
import { MediaEl, PlayerAudio } from '@rhg/components/media/types';
import { useMediaControls } from '@rhg/hooks/use-media-controls';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    AudioControls,
    AudioState,
    AudioTiming,
    LoadAudioProps,
} from './types';

export const AudioContext = createContext<AudioState>(null!);
// Separate high-frequency timing context (timingState + currentCue). Split out
// so the main AudioContext value stays referentially stable across the ~4Hz
// `timeupdate` churn — only the scrubber/timeline/subtitle UI subscribes here.
// EVE-12668. Defaults to a zeroed, paused timing so a consumer rendered
// without a provider (or a test mocking only `useAudio`) reads a safe idle
// state rather than throwing.
export const AudioTimingContext = createContext<AudioTiming>({
    timingState: {
        isPaused: true,
        duration: 0,
        remainingTime: 0,
        currentTime: 0,
        currentPercent: 0,
        bufferedPercent: 0,
    },
    currentCue: undefined,
});

interface Props {
    children: ReactNode;
}
export const AudioProvider = ({ children }: Props) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [autoplay, setAutoplay] = useState(false);
    const [hasAutoPlayRestriction, setHasAutoPlayRestriction] = useState(false);
    const [loop, setLoop] = useState(false);
    const [audio, setAudio] = useState<PlayerAudio | null>(null);
    const [mediaEl, setMediaEl] = useState<MediaEl>();

    // While true, event-driven timing updates are skipped so the ~4Hz
    // `timeupdate` churn can't block a Safari/Firefox route-transition commit —
    // without pausing the audio. A ref (not state) so toggling it does NOT
    // itself cause a render, and `setTiming` reads the live value at event
    // time. Set only by useNavigationAudioSync (Safari/FF). EVE-13083.
    const willSkipTimingUpdateRef = useRef(false);
    const setWillSkipTimingUpdate = useCallback((willSkip: boolean) => {
        willSkipTimingUpdateRef.current = willSkip;
    }, []);
    const shouldSkipTimingUpdate = useCallback(
        () => willSkipTimingUpdateRef.current,
        []
    );

    const controls = useMediaControls({
        type: 'audio',
        mediaEl: mediaEl ?? null,
        isInitiallyMuted: false,
        persistPlaybackRate: true,
        shouldSkipTimingUpdate,
    });

    const {
        toggleMuted,
        timingState,
        currentCue,
        isFullscreen,
        isMuted,
        isShowingSubtitles,
        hasSubtitles,
        isPaused,
        activeSrc,
        togglePlay,
        toggleSubtitles,
        toggleFullscreen,
        setTime,
        playbackRate,
        setPlaybackRate,
    } = controls;

    // Low-frequency controls for the main context. Excludes timingState /
    // currentCue, so its identity only changes on a genuine play/pause/track /
    // mute / rate change — not on every `timeupdate`. EVE-12668.
    const audioControls = useMemo<AudioControls>(
        () => ({
            isFullscreen,
            isMuted,
            isShowingSubtitles,
            hasSubtitles,
            isPaused,
            activeSrc,
            togglePlay,
            toggleSubtitles,
            toggleFullscreen,
            toggleMuted,
            setTime,
            playbackRate,
            setPlaybackRate,
        }),
        [
            isFullscreen,
            isMuted,
            isShowingSubtitles,
            hasSubtitles,
            isPaused,
            activeSrc,
            togglePlay,
            toggleSubtitles,
            toggleFullscreen,
            toggleMuted,
            setTime,
            playbackRate,
            setPlaybackRate,
        ]
    );

    const timingValue = useMemo<AudioTiming>(
        () => ({ timingState, currentCue }),
        [timingState, currentCue]
    );

    const credentials = useMemo(
        () => (audio?.needsCredentials ? `?${audio?.credentials}` : ''),
        [audio?.credentials, audio?.needsCredentials]
    );

    const sources = useMemo(
        () =>
            audio?.src && audio?.type
                ? [
                      {
                          src: `${audio.src}${credentials}`,
                          type: audio?.type,
                      },
                  ]
                : undefined,
        [audio?.src, audio?.type, credentials]
    );

    const subtitleSources = useMemo(
        () =>
            audio?.subtitleSrc && audio?.subtitleType
                ? [
                      {
                          src: `${audio.subtitleSrc}${credentials}`,
                          type: audio.subtitleType,
                      },
                  ]
                : undefined,
        [audio?.subtitleSrc, audio?.subtitleType, credentials]
    );

    const options = useMemo(
        () => ({
            autoplay,
            loop,
            controls: false,
        }),
        [autoplay, loop]
    );

    const loadAudio = useCallback(
        ({
            audio: audio_,
            autoplay: autoplay_,
            loop: loop_,
            isInitiallyMuted,
        }: LoadAudioProps) => {
            setAutoplay(!!autoplay_);
            setLoop(!!loop_);
            // Only override mute when the caller is explicit. Omitting
            // isInitiallyMuted (e.g. playlist track changes) preserves the
            // user's current mute state across loads.
            if (typeof isInitiallyMuted === 'boolean') {
                toggleMuted(isInitiallyMuted);
            }
            setAudio(audio_);
            setIsLoaded(true);
        },
        [toggleMuted]
    );

    const unloadAudio = useCallback(() => {
        setIsLoaded(false);
        setMediaEl((mediaEl) => {
            if (mediaEl) {
                mediaEl.currentTime = 0;
            }
            return undefined;
        });
        setAudio(null);
        setAutoplay(false);
        setLoop(false);
        // Mute is intentionally not reset here — it's a user preference that
        // persists across track/session changes (unloadAudio runs between
        // playlist tracks via closeGuidePlayer).
    }, []);

    const value = useMemo<AudioState>(
        () => ({
            loadAudio,
            unloadAudio,
            setLoop,
            mediaEl,
            audio,
            controls: audioControls,
            isLoaded: isLoaded && !!mediaEl,
            hasAutoPlayRestriction,
            setWillSkipTimingUpdate,
        }),
        [
            loadAudio,
            unloadAudio,
            setLoop,
            mediaEl,
            audio,
            audioControls,
            isLoaded,
            hasAutoPlayRestriction,
            setWillSkipTimingUpdate,
        ]
    );

    return (
        <AudioContext.Provider value={value}>
            <AudioTimingContext.Provider value={timingValue}>
                {children}
                {isLoaded && (
                    <Audio
                        sources={sources}
                        subtitleSources={subtitleSources}
                        options={options}
                        setMediaEl={setMediaEl}
                        needsCredentials={audio?.needsCredentials}
                        setHasAutoPlayRestriction={setHasAutoPlayRestriction}
                    />
                )}
            </AudioTimingContext.Provider>
        </AudioContext.Provider>
    );
};
